/* eslint-disable max-statements */
import { Button, getDefaultZIndex, Grid, Group, LoadingOverlay, Modal, Stack, Switch, Text } from '@mantine/core';
import { UseFormReturnType } from '@mantine/form';
import { useEffect, useState } from 'react';

import { DeclarationHighValueGroupType, EnvironmentCountries } from '@e-origin/shared';

import { EucdmCode, MultiLangCodes, Select, TextInput } from '@components';
import { ALLOWED_HV_REQUESTED_PROCEDURES } from '@constants';
import { useDeclarant } from '@hooks/declarant';
import { useCodeListSystem } from '@hooks/useCodeListSystem';
import { useSettings } from '@hooks/useSettings';
import { isHighValueDeclaration } from '@utils';
import { BeforeSendingFormData } from './beforeSendingBatchFormIntializer';

interface FinalizeProcedureProps {
  form: UseFormReturnType<BeforeSendingFormData>;
  disabled: boolean;
  isExport: boolean;
  onSave: (newGroup: string) => Promise<void>;
}

const GENERAL_INFO_GROUP_FIELD = 'generalInfo.group';

export const FinalizeProcedure: React.FC<FinalizeProcedureProps> = (props) => {
  const { form, isExport, onSave } = props;
  const { data: declarant, isLoading } = useDeclarant();
  const { data: settings } = useSettings();
  const [pendingGroupChange, setPendingGroupChange] = useState<string | null>(null);

  const codelistSystems = useCodeListSystem({
    group: form.values.generalInfo?.group,
    isExport: isExport,
  });

  const ProcedureHeaderSwitch = () => (
    <Switch {...form.getInputProps('goodShipmentGlobal.procedureHeader')} checked={form.values.goodShipmentGlobal.procedureHeader} size="xs" />
  );

  if (!declarant) {
    return <LoadingOverlay visible={isLoading} overlayProps={{ radius: 'sm', blur: 2 }} />;
  }

  const selectedMessageType = form.values.generalInfo.group;
  const allowedProcedures = ALLOWED_HV_REQUESTED_PROCEDURES[selectedMessageType] ?? [];
  const declarationGroup = form.values.generalInfo?.group as DeclarationHighValueGroupType;

  useEffect(() => {
    if (form.values.generalInfo.group === DeclarationHighValueGroupType.H5 && !form.values.declarationGoods.additionalProcedure?.includes('F15')) {
      form.setFieldValue('declarationGoods.additionalProcedure', [...form.values.declarationGoods.additionalProcedure, 'F15']);
    }
  }, [form.values.generalInfo.group]);

  const handleDeclarationProfileChange = (newValue: string) => {
    if (!newValue || newValue === form.values.generalInfo.group) {
      return;
    }

    setPendingGroupChange(newValue);
  };

  const confirmDeclarationProfileChange = async () => {
    const newValue = pendingGroupChange;
    setPendingGroupChange(null);
    form.setFieldValue(GENERAL_INFO_GROUP_FIELD, newValue);
    await onSave(newValue);
  };

  return (
    <>
      <Modal
        opened={!!pendingGroupChange}
        onClose={() => setPendingGroupChange(null)}
        title="Confirm declaration profile change"
        zIndex={getDefaultZIndex('modal') + 2}
        centered
      >
        <Stack>
          <Text size="sm">You have chosen the declaration profile {pendingGroupChange}. Any changes you made will be saved. Please confirm.</Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setPendingGroupChange(null)}>
              Cancel
            </Button>
            <Button onClick={confirmDeclarationProfileChange}>Save</Button>
          </Group>
        </Stack>
      </Modal>

      <Grid mb="sm">
        {settings?.country === EnvironmentCountries.BE && !isExport && (
          <Grid.Col span={4}>
            <Select
              {...form.getInputProps(GENERAL_INFO_GROUP_FIELD)}
              label="Declaration profile"
              placeholder="Select a declaration profile"
              data={Object.values(DeclarationHighValueGroupType).map((value) => ({ value, label: value }))}
              disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
              searchable
              data-pw="message-type-input"
              onChange={handleDeclarationProfileChange}
            />
          </Grid.Col>
        )}
        <Grid.Col span={4}>
          <EucdmCode path="goodShipmentGlobal.customsProcedure.current">
            <MultiLangCodes
              {...form.getInputProps('customsProcedureCurrent')}
              label="Requested procedure"
              code="CL092"
              includeList={allowedProcedures.length > 0 ? allowedProcedures : undefined}
              system={codelistSystems}
              onChange={(selectedOption) => form.setFieldValue('customsProcedureCurrent', selectedOption)}
              rightSection={<ProcedureHeaderSwitch />}
              disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
              data-pw="requested-procedure-input"
            />
          </EucdmCode>
        </Grid.Col>
        <Grid.Col span={4}>
          <EucdmCode path="goodShipmentGlobal.customsProcedure.previous">
            <MultiLangCodes
              {...form.getInputProps('customsProcedurePrevious')}
              label="Previous procedure"
              code="CL093"
              system={codelistSystems}
              onChange={(selectedOption) => form.setFieldValue('customsProcedurePrevious', selectedOption)}
              rightSection={<ProcedureHeaderSwitch />}
              disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
              data-pw="previous-procedure-input"
            />
          </EucdmCode>
        </Grid.Col>
      </Grid>

      <Grid mb="sm">
        {!isExport && (
          <Grid.Col span={4}>
            <EucdmCode path="goodShipmentGlobal.preference">
              <MultiLangCodes
                {...form.getInputProps('goodShipmentGlobal.preference')}
                label="Preference"
                code="CL603"
                system={codelistSystems}
                onChange={(selectedOption) => form.setFieldValue('goodShipmentGlobal.preference', selectedOption)}
                rightSection={<ProcedureHeaderSwitch />}
                disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
                data-pw="preference-input"
              />
            </EucdmCode>
          </Grid.Col>
        )}
        <Grid.Col span={4}>
          <EucdmCode path="goodsShipmentItems.additionalProcedure">
            <MultiLangCodes
              {...form.getInputProps('declarationGoods.additionalProcedure')}
              multiple
              searchable
              clearable
              hidePickedOptions
              label="Additional procedure"
              code="AdditionalProcedure"
              system={codelistSystems}
              data-pw="additional-procedure-input"
              disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
            />
          </EucdmCode>
        </Grid.Col>
      </Grid>

      {settings?.country === EnvironmentCountries.BE && !isHighValueDeclaration(declarationGroup) && (
        <Grid mb="sm">
          <Grid.Col span={4}>
            <EucdmCode path="goodShipmentGlobal.customsProcedure.type">
              <TextInput
                {...form.getInputProps('goodShipmentGlobal.customsProcedureType')}
                label="Procedure Type"
                rightSection={<ProcedureHeaderSwitch />}
                disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
                data-pw="procedure-type-input"
              />
            </EucdmCode>
          </Grid.Col>
          <Grid.Col span={4}>
            <TextInput
              {...form.getInputProps('goodShipmentGlobal.customsNationalProcedure')}
              label="National procedure"
              rightSection={<ProcedureHeaderSwitch />}
              disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
              data-pw="national-procedure-input"
            />
          </Grid.Col>
          <Grid.Col span={4}>
            <MultiLangCodes
              {...form.getInputProps('goodShipmentGlobal.customsNationalProcedureCode')}
              label="National procedure code"
              code="CL457"
              system={codelistSystems}
              onChange={(selectedOption) => form.setFieldValue('goodShipmentGlobal.customsNationalProcedureCode', selectedOption)}
              rightSection={<ProcedureHeaderSwitch />}
              disabled={props.disabled || !form.values.goodShipmentGlobal.procedureHeader}
              data-pw="national-procedure-code-input"
            />
          </Grid.Col>
        </Grid>
      )}
    </>
  );
};
